"use client";

import { ChevronDown, ChevronRight } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/cn";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../dropdown-menu";

export interface HeaderNavMenuItem {
	id: string;
	label: string;
	/** When omitted, consumer should render non-link UI for this item (e.g. current page). */
	href?: string;
	/**
	 * When true, the item is shown but must not activate navigation (e.g. locked topic cards).
	 * Consumers decide visuals (lock icon, muted styles).
	 */
	disabled?: boolean;
}

export type HeaderNavMenuMode = "flat" | "nested";

export type HeaderNavMenuHorizontalSeparator = "dot" | "chevron" | "none";

export interface HeaderNavMenuRenderLinkContext {
	item: HeaderNavMenuItem;
	index: number;
	total: number;
	/** Resolved active item (see `activeItemId` / `resolveActiveItemId`). */
	isActive: boolean;
	/** Nested mode: last item in the trail. */
	isLeaf: boolean;
	/** When true, links must not navigate (visual-only). */
	linksDisabled: boolean;
}

export interface HeaderNavMenuProps {
	items: readonly HeaderNavMenuItem[];
	mode: HeaderNavMenuMode;
	/**
	 * Active item id. When `null` / undefined and `resolveActiveItemId` is omitted,
	 * nested mode treats the last item as active; flat mode treats none as active unless
	 * `resolveActiveItemId` is provided.
	 */
	activeItemId?: string | null;
	/** Override default active resolution (`activeItemId` match, else nested last). */
	resolveActiveItemId?: (
		items: readonly HeaderNavMenuItem[],
	) => string | null;
	horizontalSeparator?: HeaderNavMenuHorizontalSeparator;
	/** Classes on the separator element between inline items (not before the first). */
	separatorClassName?: string;
	/** Renders each primary nav control (link or static label). */
	renderLink: (ctx: HeaderNavMenuRenderLinkContext) => React.ReactNode;
	/**
	 * Optional dropdown row renderer (e.g. `DropdownMenuItem` + `asChild`).
	 * Defaults to wrapping `renderLink` in `DropdownMenuItem asChild`.
	 */
	renderDropdownRow?: (
		ctx: HeaderNavMenuRenderLinkContext,
	) => React.ReactNode;
	/** Wide layout: wrapper around the horizontal track (e.g. `hidden xl:flex`). */
	inlineWrapperClassName: string;
	/** Classes on the flex row that lays out inline links + separators. */
	inlineRowClassName?: string;
	/** Ref attached to the inline track (for measuring active pill geometry, etc.). */
	inlineTrackRef?: React.Ref<HTMLDivElement | null>;
	/** Optional overlay inside the inline track (e.g. animated pill) — rendered after links. */
	inlineTrackDecoration?: React.ReactNode;
	/**
	 * Wrap each inline item (link + optional anchor metadata).
	 * Return a fragment or element; default is `children`.
	 */
	renderInlineItemWrapper?: (
		ctx: HeaderNavMenuRenderLinkContext,
		children: React.ReactNode,
	) => React.ReactNode;
	/** Narrow / mid layout: wrapper around the dropdown (e.g. `hidden lg:flex xl:hidden`). */
	dropdownWrapperClassName: string;
	dropdownTriggerClassName: string;
	dropdownMenuContentClassName?: string;
	dropdownAlign?: "start" | "center" | "end";
	/** `aria-label` on the menu trigger. */
	dropdownAriaLabel: string;
	/** Invisible sizing label inside the trigger (widest option). */
	dropdownSizingLabel: string;
	/** Visible label on the trigger (active / current selection). */
	dropdownValueLabel: string;
	dropdownChevronClassName?: string;
	className?: string;
	/**
	 * When true, horizontal inline links stay hidden at xl and the mid breakpoint dropdown stays open-capable at xl too (forced menu collapse).
	 */
	forceCollapseToDropdown?: boolean;
	/** Disables inline links and the dropdown trigger (navigation blocked). */
	linksDisabled?: boolean;
	/**
	 * Radix dropdown modality. Prefer `false` when layered above dialog surfaces that should stay open.
	 * @default true
	 */
	dropdownModal?: boolean;
}

function defaultActiveId(
	items: readonly HeaderNavMenuItem[],
	mode: HeaderNavMenuMode,
	activeItemId: string | null | undefined,
	resolveActiveItemId: HeaderNavMenuProps["resolveActiveItemId"],
): string | null {
	if (resolveActiveItemId) {
		return resolveActiveItemId(items);
	}
	if (activeItemId != null && activeItemId !== "") {
		return activeItemId;
	}
	if (mode === "nested" && items.length > 0) {
		return items[items.length - 1]?.id ?? null;
	}
	return null;
}

function SeparatorGlyph({
	kind,
	className,
}: {
	kind: HeaderNavMenuHorizontalSeparator;
	className?: string;
}) {
	if (kind === "none") {
		return null;
	}
	if (kind === "chevron") {
		return (
			<ChevronRight
				aria-hidden
				className={cn(
					"size-3.5 shrink-0 whitespace-nowrap opacity-80",
					className,
				)}
			/>
		);
	}
	return (
		<span
			aria-hidden
			className={cn(
				"relative z-0 shrink-0 select-none whitespace-nowrap px-0.5 text-lg leading-none text-foreground/45",
				className,
			)}
		>
			·
		</span>
	);
}

export function HeaderNavMenu({
	items,
	mode,
	activeItemId,
	resolveActiveItemId,
	horizontalSeparator = "dot",
	separatorClassName,
	renderLink,
	renderDropdownRow,
	inlineWrapperClassName,
	inlineRowClassName,
	inlineTrackRef,
	inlineTrackDecoration,
	renderInlineItemWrapper,
	dropdownWrapperClassName,
	dropdownTriggerClassName,
	dropdownMenuContentClassName,
	dropdownAlign = "center",
	dropdownAriaLabel,
	dropdownSizingLabel,
	dropdownValueLabel,
	dropdownChevronClassName,
	className,
	forceCollapseToDropdown = false,
	linksDisabled = false,
	dropdownModal = true,
}: HeaderNavMenuProps) {
	const total = items.length;
	const resolvedActiveId = defaultActiveId(
		items,
		mode,
		activeItemId,
		resolveActiveItemId,
	);

	const wrapInline =
		renderInlineItemWrapper ??
		((_ctx, children) => (
			<span className="relative inline-flex items-center">
				{children}
			</span>
		));

	const defaultDropdownRow = (ctx: HeaderNavMenuRenderLinkContext) => {
		if (ctx.linksDisabled || ctx.item.disabled) {
			return (
				<DropdownMenuItem
					className={cn(
						"pointer-events-auto cursor-not-allowed font-semibold opacity-60",
						ctx.item.disabled && "gap-2 opacity-70",
					)}
					onSelect={(event) => {
						event.preventDefault();
					}}
				>
					{ctx.item.label}
				</DropdownMenuItem>
			);
		}
		if (!ctx.item.href) {
			return (
				<DropdownMenuItem
					className="pointer-events-auto cursor-default font-semibold opacity-100"
					onSelect={(event) => {
						event.preventDefault();
					}}
				>
					{ctx.item.label}
				</DropdownMenuItem>
			);
		}
		return (
			<DropdownMenuItem asChild className="cursor-pointer">
				{renderLink(ctx)}
			</DropdownMenuItem>
		);
	};

	const rowForDropdown = renderDropdownRow ?? defaultDropdownRow;

	if (total === 0) {
		return null;
	}

	return (
		<div className={cn("flex min-w-0 max-w-full flex-col", className)}>
			<div
				className={cn(
					"min-w-0 justify-center",
					forceCollapseToDropdown ? "hidden" : inlineWrapperClassName,
				)}
			>
				<div
					ref={inlineTrackRef}
					className={cn(
						"relative z-0 flex items-center justify-center",
						inlineRowClassName,
					)}
				>
					{items.map((item, index) => {
						const isLeaf = mode === "nested" && index === total - 1;
						const isActive = resolvedActiveId === item.id;
						const ctx: HeaderNavMenuRenderLinkContext = {
							item,
							index,
							total,
							isActive,
							isLeaf,
							linksDisabled,
						};
						return (
							<React.Fragment key={item.id}>
								{index > 0 && horizontalSeparator !== "none" ? (
									<SeparatorGlyph
										kind={horizontalSeparator}
										className={separatorClassName}
									/>
								) : null}
								{wrapInline(ctx, renderLink(ctx))}
							</React.Fragment>
						);
					})}
					{inlineTrackDecoration}
				</div>
			</div>

			<div
				className={cn(
					"min-w-0 justify-center",
					forceCollapseToDropdown
						? "hidden justify-center lg:flex"
						: dropdownWrapperClassName,
				)}
			>
				<DropdownMenu modal={dropdownModal}>
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							disabled={linksDisabled}
							className={cn(
								dropdownTriggerClassName,
								linksDisabled &&
									"cursor-not-allowed opacity-60",
							)}
							aria-label={dropdownAriaLabel}
						>
							<span className="inline-grid min-w-0 max-w-full shrink">
								<span
									className="col-start-1 row-start-1 select-none whitespace-nowrap opacity-0"
									aria-hidden
								>
									{dropdownSizingLabel}
								</span>
								<span className="col-start-1 row-start-1 min-w-0 truncate whitespace-nowrap">
									{dropdownValueLabel}
								</span>
							</span>
							<ChevronDown
								className={cn(
									"size-4 shrink-0 opacity-90",
									dropdownChevronClassName,
								)}
								aria-hidden
							/>
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align={dropdownAlign}
						className={cn(
							"z-[230] min-w-0 w-max max-w-[calc(100vw-2rem)]",
							dropdownMenuContentClassName,
						)}
					>
						{items.map((item, index) => {
							const isLeaf =
								mode === "nested" && index === total - 1;
							const isActive = resolvedActiveId === item.id;
							const ctx: HeaderNavMenuRenderLinkContext = {
								item,
								index,
								total,
								isActive,
								isLeaf,
								linksDisabled,
							};
							return (
								<React.Fragment key={item.id}>
									{rowForDropdown(ctx)}
								</React.Fragment>
							);
						})}
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</div>
	);
}
